import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import { Close as CloseIcon, Add as AddIcon } from "@mui/icons-material";
import { createNewTask } from "store/features/company/projectManagement/task/taskActions";
import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import {
  selectProjects,
  selectProjectLoading,
} from "store/features/company/projectManagement/project/projectSelectors";
import {
  selectTaskLoading,
  selectEmployeeTaskDateRanges,
} from "store/features/company/projectManagement/task/taskSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import {
  selectEmployees,
  selectEmployeeLoading,
} from "store/features/company/employee/employeeSelectors";
import {
  fetchTasks,
  fetchEmployeeTaskDateRanges,
} from "store/features/company/projectManagement/task/taskActions";
import { selectTasks } from "store/features/company/projectManagement/task/taskSelectors";
import { fetchMilestones } from "store/features/company/projectManagement/milestone/milestoneActions";
import {
  selectMilestones,
  selectMilestonesLoading,
} from "store/features/company/projectManagement/milestone/milestoneSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import addTaskSvg from "assets/modals/add.svg";
import DateRangeCalendar from "components/calendar/DateRangeCalendar";


const toInputDate = (value) => (value ? String(value).split("T")[0] : "");

const getTaskDateError = ({
  startDate,
  dueDate,
  minDate,
  maxDate,
  parentLabel,
}) => {
  if (!startDate || !dueDate) return "Start date and due date are required";

  if (startDate && dueDate && startDate > dueDate) {
    return "Start date cannot be after due date";
  }

  if (startDate && minDate && startDate < minDate) {
    return `Task cannot start before the ${parentLabel}`;
  }

  if (dueDate && maxDate && dueDate > maxDate) {
    return `Task cannot exceed the ${parentLabel} deadline`;
  }

  return "";
};

const AddTaskModal = ({ isOpen, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const rawProjects = useSelector(selectProjects);
  const projectsLoading = useSelector(selectProjectLoading);
  const employees = useSelector(selectEmployees);
  const employeesLoading = useSelector(selectEmployeeLoading);
  const currentUser = useSelector(selectCurrentUser);
  const role = getSecureLocalItem("role") || "admin";
  const userRaw = getSecureLocalItem("user");
  const storedUser = userRaw
    ? typeof userRaw === "string"
      ? JSON.parse(userRaw)
      : userRaw
    : null;
  const user = currentUser || storedUser;

  const currentUserId = user?.id || user?.user_id;
  const currentEmployee = employees.find(
    (emp) =>
      (user?.employee_id && String(emp.id) === String(user.employee_id)) ||
      (user?.id && String(emp.user_id) === String(user.id)) ||
      (user?.id && String(emp.id) === String(user.id)) ||
      (user?.email &&
        emp.email &&
        emp.email.toLowerCase() === user.email.toLowerCase()),
  );
  const currentEmployeeId =
    currentEmployee?.id || user?.employee_id || user?.id;

  const projects = useMemo(() => {
    if (!rawProjects || !Array.isArray(rawProjects)) return [];
    if (role === "Company") return rawProjects;
    if (!currentUserId && !currentEmployeeId) return rawProjects;

    return rawProjects.filter((project) => {
      // 1. Is Project Lead
      const managerId = project.project_manager_id;
      const isProjectLead =
        managerId !== undefined &&
        managerId !== null &&
        ((currentEmployeeId &&
          String(managerId) === String(currentEmployeeId)) ||
          (currentUserId && String(managerId) === String(currentUserId)));

      // 2. Is Team Member
      const teamMembers =
        project.team_members || project.teamMembers || project.members || [];

      const isTeamMember =
        Array.isArray(teamMembers) &&
        teamMembers.some(
          (memberId) =>
            (currentEmployeeId &&
              String(memberId) === String(currentEmployeeId)) ||
            (currentUserId && String(memberId) === String(currentUserId)),
        );

      return isProjectLead || isTeamMember;
    });
  }, [rawProjects, currentUserId, currentEmployeeId, role]);

  const tasks = useSelector(selectTasks);
  const tasksLoading = useSelector(selectTaskLoading);
  const milestones = useSelector(selectMilestones);
  const milestonesLoading = useSelector(selectMilestonesLoading);
  const employeeTaskDateRanges = useSelector(selectEmployeeTaskDateRanges);

  const [parentTaskOptions, setParentTaskOptions] = useState([]);
  const [projectEmployees, setProjectEmployees] = useState([]);
  const [otherEmployees, setOtherEmployees] = useState([]);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);




  const [formData, setFormData] = useState({
    project_id: "",
    parent_task_id: "",


    milestone_id: "",
    title: "",
    description: "",
    assigned_to: [],
    task_mode: "individual",
    priority: "medium",
    mode_of_assigning: "Written",

    status: "todo",
    estimated_hours: "",
    start_date: "",
    due_date: "",
    labels: [],
  });
  const [labelInput, setLabelInput] = useState("");

  const [hasDateConflict, setHasDateConflict] = useState(false);
  const selectedProject = projects.find(
    (project) => String(project.id) === String(formData.project_id),
  );
  const selectedMilestone = milestones.find(
    (milestone) => String(milestone.id) === String(formData.milestone_id),
  );
  const taskDateBounds = selectedMilestone
    ? {
        minDate: toInputDate(selectedMilestone.start_date),
        maxDate: toInputDate(selectedMilestone.due_date),
        parentLabel: "milestone",
      }
    : {
        minDate: toInputDate(selectedProject?.start_date),
        maxDate: toInputDate(selectedProject?.end_date),
        parentLabel: "project",
      };
  const dateError = getTaskDateError({
    startDate: formData.start_date,
    dueDate: formData.due_date,
    ...taskDateBounds,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "project_id" ? parseInt(value) || "" : value,
      ...(name === "project_id" ? { milestone_id: "" } : {}),
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Please upload a valid file (CSV, XLSX, XLS, PDF, JPEG, JPG, PNG)");
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB");
      return;
    }

    setAttachmentFile(file);

    // Create preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachmentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // For non-image files, show file name instead
      setAttachmentPreview(null);
    }
  };

  const handleRemoveFile = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    // Reset the file input
    const fileInput = document.getElementById("file-upload");
    if (fileInput) fileInput.value = "";
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle multiple assignees
  const handleAssigneeChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      assigned_to: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleAddLabel = () => {
    if (labelInput.trim() && !formData.labels.includes(labelInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        labels: [...prev.labels, labelInput.trim()],
      }));
      setLabelInput("");
    }
  };

  const handleRemoveLabel = (labelToRemove) => {
    setFormData((prev) => ({
      ...prev,
      labels: prev.labels.filter((label) => label !== labelToRemove),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddLabel();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.project_id) {
      return;
    }
    if (dateError) {
      return;
    }

    const assignedTo = Array.isArray(formData.assigned_to)
      ? formData.assigned_to
      : formData.assigned_to
      ? [formData.assigned_to]
      : [];

    setLoading(true);

    // Create FormData for file upload
    const submitData = new FormData();

    // ✅ IMPORTANT: Parse numeric values as integers
    submitData.append("project_id", parseInt(formData.project_id)); // Convert to integer
    submitData.append("title", formData.title);
    submitData.append("description", formData.description || "");
    submitData.append(
      "assigned_to",
      JSON.stringify(assignedTo.length > 0 ? assignedTo : []),
    );
    submitData.append("task_mode", formData.task_mode);
    submitData.append("priority", formData.priority);
    submitData.append(
      "mode_of_assigning",
      formData.mode_of_assigning || "Written",
    );

    submitData.append("status", formData.status);
    submitData.append(
      "estimated_hours",
      formData.estimated_hours ? parseFloat(formData.estimated_hours) : 0,
    );
    submitData.append("start_date", formData.start_date || "");
    submitData.append("due_date", formData.due_date || "");
    submitData.append("labels", JSON.stringify(formData.labels));

    // ✅ Parse these as integers too
    if (formData.parent_task_id) {
      submitData.append("parent_task_id", parseInt(formData.parent_task_id));
    }
    if (formData.milestone_id) {
      submitData.append("milestone_id", parseInt(formData.milestone_id));
    }
    if (false && formData.task_group_id) {
      submitData.append("task_group_id", parseInt(formData.task_group_id));
    }
    if (
      false &&
      formData.depends_on_task_ids.length > 0
    ) {
      submitData.append(
        "depends_on_task_ids",
        JSON.stringify(formData.depends_on_task_ids),
      );
    }

    // Append file if selected
    if (attachmentFile) {
      submitData.append("attachments", attachmentFile);
    }

    const result = await dispatch(createNewTask(submitData));

    if (result.success) {
      onSuccess();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      project_id: "",
      parent_task_id: "",
  
  
      milestone_id: "",
      title: "",
      description: "",
      assigned_to: [],
      task_mode: "individual",
      priority: "medium",
      mode_of_assigning: "Written",

      status: "todo",
      estimated_hours: "",
      start_date: "",
      due_date: "",
      labels: [],
    });
    setLabelInput("");
    setAttachmentFile(null);
    setAttachmentPreview(null);
    const fileInput = document.getElementById("file-upload");
    if (fileInput) fileInput.value = "";
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchProjects());
      dispatch(fetchEmployees());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.width = "auto";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.width = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    if (formData.project_id) {
      const filteredTasks = tasks.filter(
        (task) => task.project_id == formData.project_id,
      );
      setParentTaskOptions(filteredTasks);
      dispatch(fetchMilestones(formData.project_id));
      /* setTaskGroupsLoading(true);
      getTaskGroups({ project_id: formData.project_id })
        .then((res) => {
          if (res?.data?.data) {
            setTaskGroups(res.data.data);
          }
        })
        .catch((err) => console.error("Error fetching task groups:", err))
        .finally(() => setTaskGroupsLoading(false)); */
    } else {
      setParentTaskOptions([]);
      // setTaskGroups([]);
    }
  }, [formData.project_id, tasks, dispatch]);

  useEffect(() => {
    if (!formData.project_id) {
      setProjectEmployees([]);
      setOtherEmployees([]);
      return;
    }

    const selectedProject = projects.find(
      (p) => p.id === Number(formData.project_id),
    );

    if (!selectedProject) return;

    const memberIds = selectedProject.team_members || [];

    setProjectEmployees(employees.filter((emp) => memberIds.includes(emp.id)));

    setOtherEmployees(employees.filter((emp) => !memberIds.includes(emp.id)));
  }, [formData.project_id, projects, employees]);

  // Check date conflicts for multiple assignees
  useEffect(() => {
    if (!isOpen || !formData.assigned_to?.length) {
      setHasDateConflict(false);
      return;
    }

    dispatch(fetchEmployeeTaskDateRanges(formData.assigned_to[0]));
  }, [isOpen, formData.assigned_to, dispatch]);

  useEffect(() => {
    if (
      !isOpen ||
      !formData.start_date ||
      !formData.due_date ||
      !employeeTaskDateRanges?.length
    ) {
      setHasDateConflict(false);
      return;
    }

    if (formData.assigned_to.length > 1) {
      setHasDateConflict(false);
      return;
    }

    const newStart = new Date(formData.start_date);
    const newEnd = new Date(formData.due_date);

    const overlap = employeeTaskDateRanges.some((range) => {
      const existingStart = new Date(range.start);
      const existingEnd = new Date(range.end);

      return newStart <= existingEnd && newEnd >= existingStart;
    });

    setHasDateConflict(overlap);
  }, [
    isOpen,
    formData.start_date,
    formData.due_date,
    formData.assigned_to,
    employeeTaskDateRanges,
  ]);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "primary.main",
    },
    "& .MuiInputLabel-asterisk": {
      color: "#ef4444",
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
              .MuiPopover-root { z-index: 1400 !important; }
              .MuiPopper-root { z-index: 1400 !important; }
              .MuiMenu-root { z-index: 1400 !important; }
            `}
          </style>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "800px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addTaskSvg}
                      alt="Add Task"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Add Task
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Add a new task to your project
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Project"
                      name="project_id"
                      value={formData.project_id}
                      onChange={handleChange}
                      required
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                      disabled={projectsLoading}
                    >
                      <MenuItem value="">
                        <em>Select a project</em>
                      </MenuItem>
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name} ({project.code})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  <TextField
                    fullWidth
                    label="Task Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter task title"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Brief description of the task"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  {/* Multi-select for Assignees */}
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel id="assignee-select-label" required>
                      Assign To
                    </InputLabel>
                    <Select
                      labelId="assignee-select-label"
                      id="assignee-select"
                      multiple
                      name="assigned_to"
                      value={formData.assigned_to}
                      required
                      onChange={handleAssigneeChange}
                      input={<OutlinedInput label="Assign To" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const employee = employees.find(
                              (emp) => emp.id === value,
                            );
                            return (
                              <Chip
                                key={value}
                                label={
                                  employee
                                    ? `${employee.first_name} ${employee.last_name}`
                                    : value
                                }
                                size="small"
                                sx={{
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "#334155"
                                      : "#f3f4f6",
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      disabled={employeesLoading}
                    >
                      {projectEmployees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_code})
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select multiple employees for team tasks
                    </FormHelperText>
                  </FormControl>

                  {/* Task Mode Selection */}
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel id="task-mode-label">Task Mode</InputLabel>
                    <Select
                      labelId="task-mode-label"
                      id="task-mode"
                      name="task_mode"
                      value={formData.task_mode}
                      onChange={handleChange}
                      label="Task Mode"
                    >
                      <MenuItem value="individual">
                        Individual - Each person completes independently
                      </MenuItem>
                      <MenuItem value="shared">
                        Shared - One completion completes for all
                      </MenuItem>
                    </Select>
                    <FormHelperText>
                      {formData.task_mode === "shared"
                        ? "When one person completes, the task is done for everyone"
                        : "Each person tracks their own progress independently"}
                    </FormHelperText>
                  </FormControl>

                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel id="milestone-select-label">
                      Milestone
                    </InputLabel>
                    <Select
                      labelId="milestone-select-label"
                      id="milestone-select"
                      name="milestone_id"
                      value={formData.milestone_id}
                      onChange={handleChange}
                      label="Milestone"
                      disabled={!formData.project_id || milestonesLoading}
                    >
                      <MenuItem value="">
                        <em>No milestone</em>
                      </MenuItem>
                      {milestones
                        .filter(
                          (m) =>
                            String(m.project_id) ===
                            String(formData.project_id),
                        )
                        .map((milestone) => (
                          <MenuItem key={milestone.id} value={milestone.id}>
                            {milestone.title}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      {formData.project_id
                        ? "Optionally link this task to a milestone"
                        : "Select a project first to see its milestones"}
                    </FormHelperText>
                  </FormControl>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      fullWidth
                      label="Estimated Hours"
                      name="estimated_hours"
                      type="number"
                      value={formData.estimated_hours}
                      onChange={handleNumberChange}
                      placeholder="10"
                      helperText="Estimated hours to complete"
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      fullWidth
                      select
                      label="Priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="urgent">Urgent</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </TextField>

                    <TextField
                      fullWidth
                      select
                      label="Mode of Assigning"
                      name="mode_of_assigning"
                      value={formData.mode_of_assigning}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="Email">Email</MenuItem>
                      <MenuItem value="Written">Written</MenuItem>
                      <MenuItem value="Verbal">Verbal</MenuItem>
                      <MenuItem value="Meeting Resolutions">
                        Meeting Resolutions
                      </MenuItem>
                      <MenuItem value="Regular Assignments">
                        Regular Assignments
                      </MenuItem>
                      <MenuItem value="Self">Self</MenuItem>
                    </TextField>
                  </Stack>

                  <DateRangeCalendar
                    startDate={formData.start_date}
                    dueDate={formData.due_date}
                    onChange={({ startDate, endDate }) => {
                      setFormData((prev) => ({
                        ...prev,
                        start_date: startDate,
                        due_date: endDate,
                      }));
                    }}
                    minDate={taskDateBounds.minDate}
                    maxDate={taskDateBounds.maxDate}
                    label="Task Timeline"
                    startLabel="Start Date"
                    endLabel="Due Date"
                    required
                    error={dateError}
                    accentColor="#f59e0b"
                  />

                  {hasDateConflict && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: "#f59e0b",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(245,158,11,0.08)"
                            : "#fffbeb",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "primary.main",
                          fontWeight: 600,
                        }}
                      >
                        Assigned member already have tasks within the selected
                        date range.
                      </Typography>
                    </Box>
                  )}
                  {/* File Attachment Section */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      Attachment (Optional)
                    </Typography>

                    {!attachmentFile ? (
                      <Box
                        sx={{
                          border: `2px dashed ${
                            theme.palette.mode === "dark"
                              ? "#475569"
                              : "#d1d5db"
                          }`,
                          borderRadius: "12px",
                          p: 3,
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "&:hover": {
                            borderColor: "#f59e0b",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#334155"
                                : "#fffbeb",
                          },
                        }}
                        onClick={() =>
                          document.getElementById("file-upload").click()
                        }
                      >
                        <input
                          id="file-upload"
                          type="file"
                          accept=".csv,.xlsx,.xls,.pdf,.jpeg,.jpg,.png"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#94a3b8"
                                : "#6b7280",
                          }}
                        >
                          Click to upload or drag and drop
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#64748b"
                                : "#9ca3af",
                          }}
                        >
                          CSV, XLSX, XLS, PDF, JPEG, JPG, PNG (Max 5MB)
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2,
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#475569"
                              : "#e5e7eb"
                          }`,
                        }}
                      >
                        {attachmentPreview ? (
                          // Image preview
                          <Box
                            component="img"
                            src={attachmentPreview}
                            alt="Attachment preview"
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: `1px solid ${
                                theme.palette.mode === "dark"
                                  ? "#475569"
                                  : "#e5e7eb"
                              }`,
                            }}
                          />
                        ) : (
                          // File icon for non-image files
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "8px",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#334155"
                                  : "#e5e7eb",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#94a3b8"
                                  : "#6b7280",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                textTransform: "uppercase",
                              }}
                            >
                              {attachmentFile.name.split(".").pop()}
                            </Typography>
                          </Box>
                        )}

                        <Box flex={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color:
                                theme.palette.mode === "dark"
                                  ? "#e2e8f0"
                                  : "#1f2937",
                            }}
                          >
                            {attachmentFile.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                theme.palette.mode === "dark"
                                  ? "#94a3b8"
                                  : "#6b7280",
                            }}
                          >
                            {(attachmentFile.size / 1024).toFixed(1)} KB
                          </Typography>
                        </Box>

                        <IconButton
                          onClick={handleRemoveFile}
                          size="small"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#94a3b8"
                                : "#6b7280",
                            "&:hover": {
                              color: "#ef4444",
                              backgroundColor: "rgba(239, 68, 68, 0.1)",
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  {/* Labels Section */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      Labels
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        placeholder="Add labels..."
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e293b"
                                : "#f9fafb",
                          },
                        }}
                      />
                      <Button
                        onClick={handleAddLabel}
                        variant="outlined"
                        sx={{
                          borderRadius: "12px",
                          borderColor: "#f59e0b",
                          color: "primary.main",
                          "&:hover": {
                            borderColor: "#d97706",
                            backgroundColor: "rgba(245,158,11,0.1)",
                          },
                        }}
                      >
                        <AddIcon />
                      </Button>
                    </Stack>
                    {formData.labels.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 1, flexWrap: "wrap", overflowX: "hidden" }}
                      >
                        {formData.labels.map((label, index) => (
                          <Chip
                            key={index}
                            label={label}
                            onDelete={() => handleRemoveLabel(label)}
                            size="small"
                            sx={{
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#334155"
                                  : "#f3f4f6",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#374151",
                              "& .MuiChip-deleteIcon": {
                                color: "primary.main",
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    loading ||
                    !formData.title.trim() ||
                    !formData.project_id ||
                    Boolean(dateError)
                  }
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Add Task"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddTaskModal;
